import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  Container,
  Grid,
  GridItem,
  Heading,
  HStack,
  Stack,
  Tag,
  TagCloseButton,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';
import CartBillDiscountPromo from 'components/carts/CartBillDiscountPromo';
import CartCoupon from 'components/carts/CartCoupon';
import CartDiscountSummary from 'components/carts/CartDiscountSummary';
import CartItem from 'components/carts/CartItem';
import CartShippingNotice from 'components/carts/CartShippingNotice';
import PageHeader from 'components/page-header/PageHeader';
import PageTitleMobile from 'components/page-header/PageTitleMobile';
import UtilPriceFormatter from 'components/utils/UtilPriceFormatter';
import useCart from 'hooks/useCart';
import useCatchError from 'hooks/useCatchError';
import useShop from 'hooks/useShop';
import useUser from 'hooks/useUser';
import { ICart } from 'interfaces/cart.interface';
import { withIronSessionSsr } from 'iron-session/next';
import RootLayout from 'layouts/RootLayout';
import { sessionOptions } from 'libs/iron-session';
import { InferGetServerSidePropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Error from 'next/error';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartRepository } from 'respository/cart.repository';
import { NextPageWithLayout } from './_app';
import { PromotionTypeEnum } from 'interfaces/promotion.interface';
import { notification } from 'antd';
import CartCouponItem from 'components/carts/CartCouponItem';

type CartPageProps = {
  cartProp?: ICart | null;
};

const CartPage: NextPageWithLayout<CartPageProps> = ({
  cartProp,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  // use router
  const router = useRouter();

  // use catchError
  const catchError = useCatchError();

  // use user
  const { user } = useUser();

  const [isDesktop] = useMediaQuery('(min-width: 992px)');

  // use translation
  const { t } = useTranslation(['cart']);

  // get cart
  const {
    cart,
    cartIsAllSelected,
    cartIsInterMediateSelected,
    cartSelectedCount,
    cartSubTotal,
    cartTotal,
    cartShippingTotal,
    cartCouponDiscountTotal,
    cartBillDiscountTotal,
    cartBillDiscountNextTier,
    cartBillDiscountPromotions,
    cartWeightTotal,
    cartItemSelectAll,
    cartSetShippingMethod,
    cartMutate,
  } = useCart(cartProp);

  // get shipping methods from shop
  const { shippingMethodAvailable } = useShop();

  // use state to store coupon remove loading
  const [couponRemoveLoading, setCouponRemoveLoading] =
    useState<boolean>(false);

  // get items
  const items = cart?.items || [];

  // handle cart submit
  const handleCartSubmit = useCallback(async () => {
    try {
      // log cart
      console.log(cart);

      if (!cart) return;

      if (!cart?.shippingMethodId) {
        return;
      }

      if (user && user.isAuthenticated) {
        const cartUpdated = await CartRepository.updateShippingMethod(
          cart?.shippingMethodId
        );

        await CartRepository.validateBeforeCheckout();

        cartMutate(cartUpdated);
      }

      router.push('/checkout');
    } catch (error) {
      catchError(error);
    }
  }, [cart]);

  useEffect(() => {
    if (cartWeightTotal) {
      if (cartWeightTotal > 2000) {
        cartSetShippingMethod(1); // ems
      }
    }
  }, [cartWeightTotal]);

  return (
    <>
      <Head>
        <title>
          {t('cart.title', {
            // TH: สินค้าในตะกร้า
            // EN: Cart
          })}
        </title>
      </Head>
      <Box>
        <PageHeader title={t('cart.title')}></PageHeader>
        <PageTitleMobile>{t('cart.title')}</PageTitleMobile>
        <Container
          pt={{
            base: 6,
            lg: 10,
          }}
          pb={{
            base: 0,
            lg: 20,
          }}
        >
          {cart && items.length > 0 ? (
            <>
              <HStack
                mb={3}
                alignItems="flex-end"
                display={{
                  base: 'none',
                  lg: 'flex',
                }}
              >
                <Heading fontSize="2xl" fontWeight={400}>
                  {t('cart.title')}
                </Heading>
                <Text>
                  (
                  {t('cart.count', {
                    // TH: {{count}} รายการ
                    // EN: {{count}} items
                    count: items.length,
                  })}
                  )
                </Text>
              </HStack>
              <Grid
                templateColumns={{
                  base: 'repeat(1, 1fr)',
                  md: 'repeat(2, 1fr)',
                  lg: '1fr 280px',
                }}
                columnGap={7}
              >
                <GridItem>
                  <Box
                    borderBottom={{
                      base: '10px solid #F2F2F2',
                      lg: 'none',
                    }}
                    mx={{
                      base: -4,
                      lg: 0,
                    }}
                    pb={2}
                    px={{
                      base: 4,
                      lg: 0,
                    }}
                  >
                    <HStack
                      borderTop="1px solid #e2e2e2"
                      borderBottom="1px solid #e2e2e2"
                      mb={4}
                      py={2}
                      px={4}
                      display={{
                        base: 'none',
                        lg: 'flex',
                      }}
                    >
                      <Button
                        boxSize="30px"
                        minW="auto"
                        padding="0"
                        bg="white"
                        variant="unstyled"
                        border="1px solid #e2e2e2"
                        justifyContent="center"
                        alignItems="center"
                        _hover={{
                          borderColor: 'primary.500',
                        }}
                        onClick={() =>
                          cartItemSelectAll(
                            cartIsAllSelected
                              ? false
                              : cartIsInterMediateSelected
                              ? false
                              : true
                          )
                        }
                      >
                        <Box textAlign="center">
                          {cartIsAllSelected || cartIsInterMediateSelected ? (
                            <Image
                              src={
                                cartIsAllSelected
                                  ? '/image-icon/check-active.png'
                                  : '/image-icon/minus-active.png'
                              }
                              width={20}
                              height={20}
                              alt="checked"
                              style={{
                                margin: 'auto',
                                width: isDesktop ? 20 : 16,
                              }}
                            ></Image>
                          ) : null}
                        </Box>
                      </Button>
                      <Text>
                        {t('cart.selectAll', {
                          // TH: เลือกทั้งหมด
                          // EN: Select all
                        })}
                      </Text>
                    </HStack>
                    {cart && (
                      <>
                        <Stack spacing={4} mb={4}>
                          {items.map((item, index) => (
                            <CartItem key={index} item={item}></CartItem>
                          ))}
                        </Stack>
                      </>
                    )}
                  </Box>
                </GridItem>
                <GridItem>
                  <Box
                    px={{
                      base: 0,
                      lg: 4,
                    }}
                    py={{
                      base: 4,
                      lg: 4,
                    }}
                    bg={{
                      base: 'white',
                      lg: '#EADED0',
                    }}
                  >
                    <Box borderBottom="1px solid #666" mb={2.5} pb={3}>
                      <CartCoupon></CartCoupon>

                      {cart && cart.coupons && cart.coupons.length > 0 && (
                        <HStack mt={2}>
                          {cart.coupons.map((coupon, index) => (
                            <CartCouponItem
                              coupon={coupon}
                              key={index}
                            ></CartCouponItem>
                          ))}
                        </HStack>
                      )}
                    </Box>
                    {shippingMethodAvailable &&
                      shippingMethodAvailable.length > 0 && (
                        <Box borderBottom="1px solid #666" mb={2.5} pb={3}>
                          <Heading fontSize="md" mb={2} fontWeight={400}>
                            {t('cart.shippingMethod.title', {
                              // TH: วิธีการจัดส่งสินค้า (ขนส่งเอกชน)
                              // EN: Shipping Method (Private Courier)
                            })}
                          </Heading>
                          <HStack>
                            <CheckboxGroup value={[cart.shippingMethodId]}>
                              {shippingMethodAvailable.map(
                                (shippingMethod, index) => (
                                  <Checkbox
                                    key={index}
                                    flex={1}
                                    size="lg"
                                    colorScheme="primary"
                                    fontSize="md"
                                    className="checkbox-white-bg"
                                    value={shippingMethod.id}
                                    disabled={
                                      cartWeightTotal > 2000 &&
                                      3 === shippingMethod.id
                                    }
                                    onChange={(e) => {
                                      cartSetShippingMethod(+e.target.value);
                                    }}
                                    isChecked={
                                      cart.shippingMethodId ===
                                      shippingMethod.id
                                    }
                                  >
                                    <Text as="span" fontSize="md">
                                      {router.locale === 'th'
                                        ? shippingMethod.name_th
                                        : shippingMethod.name_en}
                                    </Text>
                                  </Checkbox>
                                )
                              )}
                            </CheckboxGroup>
                          </HStack>
                        </Box>
                      )}
                    <Stack
                      borderBottom="1px solid #666"
                      mb={2}
                      pb={3}
                      spacing={1}
                    >
                      <HStack justifyContent="space-between">
                        <Text>
                          {t('cart.summary.subTotal', {
                            // TH: {{count}} รายการ
                            // EN: {{count}} items
                            count: cartSelectedCount,
                          })}
                        </Text>
                        <Text>
                          <UtilPriceFormatter
                            price={cartSubTotal}
                          ></UtilPriceFormatter>
                        </Text>
                      </HStack>
                      <CartDiscountSummary
                        couponDiscountTotal={cartCouponDiscountTotal}
                        billDiscountTotal={cartBillDiscountTotal}
                      ></CartDiscountSummary>
                      <HStack
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Text>
                          {t('cart.summary.shippingTotal', {
                            // TH: ค่าจัดส่ง
                            // EN: Shipping
                          })}
                        </Text>
                        <Box textAlign="right">
                          <Text>
                            <UtilPriceFormatter
                              price={cartShippingTotal ?? 0}
                            ></UtilPriceFormatter>
                          </Text>
                          {/* <Text as="small" color="gray.500" fontWeight={500}>
                            {cartWeightTotalWithoutAdded} ก. + 30% ={' '}
                            {cartWeightTotal} ก.
                          </Text> */}
                        </Box>
                      </HStack>
                    </Stack>
                    <Box>
                      <HStack justifyContent="space-between" fontWeight={400}>
                        <Text>
                          {t('cart.summary.total', {
                            // TH: จำนวนเงินทั้งหมด
                            // EN: Total
                          })}
                        </Text>
                        <Text fontSize="lg" color="primary.500">
                          <UtilPriceFormatter
                            price={cartTotal}
                          ></UtilPriceFormatter>
                        </Text>
                      </HStack>
                    </Box>
                    <CartShippingNotice />
                  </Box>

                  <Box
                    mb={6}
                    display={{
                      md: 'none',
                    }}
                  >
                    <Text fontSize="sm" textAlign="center">
                      **ในกรณีค่าขนส่งมีการเปลี่ยนแปลง
                      ทางบริษัทขออนุญาตติดต่อกลับลูกค้า**
                    </Text>
                  </Box>

                  <HStack
                    spacing={0}
                    alignItems="initial"
                    mx={{
                      base: -4,
                      lg: 0,
                    }}
                  >
                    <Box
                      flex="0 0 auto"
                      display={{
                        base: 'block',
                        lg: 'none',
                      }}
                    >
                      <Center bg="gray.800" height="100%" px={6} minW="100px">
                        <Text color="white">
                          <UtilPriceFormatter
                            price={cartTotal ?? 0}
                          ></UtilPriceFormatter>
                        </Text>
                      </Center>
                    </Box>
                    <Button
                      flex={1}
                      fontSize="lg"
                      height={{
                        base: '50px',
                        lg: '40px',
                      }}
                      disabled={cartTotal === 0}
                      onClick={handleCartSubmit}
                    >
                      {t('cart.checkout', {
                        // TH: ดำเนินการชำระเงิน
                        // EN: Continue checkout
                      })}
                    </Button>
                  </HStack>

                  <Box
                    mt={4}
                    display={{
                      base: 'none',
                      lg: 'block',
                    }}
                  >
                    <Text fontSize="sm" textAlign="center">
                      **ในกรณีค่าขนส่งมีการเปลี่ยนแปลง<br></br>
                      ทางบริษัทขออนุญาตติดต่อกลับลูกค้า**
                    </Text>
                  </Box>

                  <CartBillDiscountPromo
                    billDiscountTotal={cartBillDiscountTotal}
                    nextTier={cartBillDiscountNextTier}
                    promotions={cartBillDiscountPromotions}
                  ></CartBillDiscountPromo>
                </GridItem>
              </Grid>
            </>
          ) : (
            <Center
              pt={{
                base: 16,
                lg: 0,
              }}
              minH={{
                base: 'auto',
                lg: '300px',
              }}
              flexDirection="column"
            >
              <Box mb={4}>
                <Image
                  src="/image-icon/shopping-large.png"
                  width={70}
                  height={70}
                  alt="empty-cart"
                  style={{
                    margin: 'auto',
                  }}
                ></Image>
              </Box>
              <Heading textAlign="center" fontSize="2xl" mb={8}>
                {t('cart.empty', {
                  // TH: คุณยังไม่มีสินค้าในตระกร้าตอนนี้
                  // EN: Your shopping basket is currently empty.
                })}
              </Heading>
              <Button
                height="55px"
                size="lg"
                fontSize="xl"
                minW={{
                  base: '100%',
                  lg: '350px',
                }}
                onClick={() => router.push('/product-categories/lighting')}
              >
                {t('cart.continueShoppingButton', {
                  // TH: เลือกสินค้าลงตะกร้า
                  // EN: Continue shopping
                })}
              </Button>
            </Center>
          )}
        </Container>
      </Box>
    </>
  );
};

CartPage.getLayout = (page) => <RootLayout>{page}</RootLayout>;

export const getServerSideProps = withIronSessionSsr<CartPageProps>(
  async function ({ req, locale, query }) {
    const _locale = locale || 'th';

    let cart: ICart | null = null;

    // get order
    try {
      const user = req.session.user;

      if (user && user.accessToken) {
        const cartResponse = await fetch(
          `${process.env.API_URL}/v1/public/cart`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${user.accessToken}`,
            },
          }
        );

        if (!cartResponse.ok) {
          return {
            redirect: {
              destination: '/error',
              permanent: false,
            },
          };
        }

        cart = await cartResponse.json();
      }
    } catch (error) {
      cart = null;
    }

    return {
      props: {
        cartProp: cart,
        ...(await serverSideTranslations(_locale, ['common', 'cart'])),
      },
    };
  },
  sessionOptions
);

export default CartPage;
